import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DataService } from 'src/app/data.service';

/**
 *  PÉNZÜGY → NORMATÍVA
 *
 *  Mennyi állami támogatás (normatíva) jár a fenntartónak a Kincstár képlete szerint, és
 *  mennyi érkezett ténylegesen a tandíj-számlára. A létszám mindenhol az AKTÍV jogviszony,
 *  október 1-jei állapottal: a tárgyév I–VIII. hava az előző év október 1-jéből, a IX–XII.
 *  a tárgyév október 1-jéből (addig becsült). A jogcímek (fajlagos összeg, osztó, forrás)
 *  évente szerkeszthetők, a létszám kézzel felülírható. Lásd backend/normativa.js.
 */
@Component({
  selector: 'app-normativa',
  templateUrl: './normativa.component.html',
  styleUrls: ['./normativa.component.scss'],
})
export class NormativaComponent implements OnInit {
  intezmeny = '';
  ev = new Date().getFullYear();
  evek: number[] = [];
  nezet: 'szamitas' | 'havi' | 'jogcimek' = 'szamitas';
  adat: any = null;
  betolt = false;
  hiba = '';
  uzenet = '';
  kezelheti = true;

  // felülírás
  felulirNyitva: string | null = null;     // `${jogcim_id}|${idoszak}`
  felulirErtek: number | null = null;
  felulirMegj = '';

  // jogcím-szerkesztés
  szerk: any = null;
  masolasForras: number | null = null;
  readonly HONAPOK = ['január', 'február', 'március', 'április', 'május', 'június', 'július', 'augusztus', 'szeptember', 'október', 'november', 'december'];

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    const si: any = await this.data.getFromStorage('sajatIntezmeny');
    this.intezmeny = String(si?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
    try {
      const a: any = await this.get(`/api/normativa/allapot/${this.intezmeny}`);
      this.kezelheti = !!a?.kezelheti;
      if (a?.ev) this.ev = Number(a.ev);
    } catch { this.kezelheti = false; }
    this.evek = [this.ev - 1, this.ev, this.ev + 1];
    if (this.kezelheti) await this.frissit();
  }

  private get(url: string) { return firstValueFrom(this.http.get<any>(url)); }
  private post(url: string, body: any) { return firstValueFrom(this.http.post<any>(url, body)); }
  private jelez(s: string) { this.uzenet = s; setTimeout(() => { if (this.uzenet === s) this.uzenet = ''; }, 4000); }

  async frissit() {
    this.hiba = '';
    this.betolt = true;
    try {
      this.adat = await this.get(`/api/normativa/szamitas/${this.intezmeny}?ev=${this.ev}`);
      this.masolasForras = this.ev - 1;
    } catch (e: any) {
      this.hiba = e?.error?.error || 'Nem sikerült betölteni.';
      this.adat = null;
    } finally { this.betolt = false; }
  }

  /** Forint magyar tagolással, tizedes nélkül. */
  ft(n: any): string {
    const v = Number(n);
    return Number.isFinite(v) ? Math.round(v).toLocaleString('hu-HU') : '–';
  }
  /** Létszám, tizedesvesszővel (legfeljebb két tizedes). */
  sz(n: any, tizedes = 2): string {
    const v = Number(n);
    if (n === null || n === undefined || !Number.isFinite(v)) return '–';
    return v.toLocaleString('hu-HU', { maximumFractionDigits: tizedes });
  }

  get aktivJogcimek(): any[] { return (this.adat?.jogcimek || []).filter((j: any) => j.aktiv); }
  alapNev(id: string) { return (this.adat?.jogcimek || []).find((j: any) => j.id === id)?.nev || '–'; }
  modszerNev(m: string) { return this.adat?.modszerek?.[m] || m; }
  forrasNev(f: string) { return this.adat?.forrasok?.[f] || f; }

  /** A határozat összevetéséhez: a várt − a határozat szerinti. */
  elteres(a: any, b: any) { return (Number(a) || 0) - (Number(b) || 0); }

  get beerkezettOssz(): number {
    return (this.adat?.honapok || []).reduce((s: number, h: any) => s + (h.beerkezett || 0) + (h.javasolt || 0), 0);
  }
  get vartEddig(): number {
    return (this.adat?.honapok || []).filter((h: any) => h.mult).reduce((s: number, h: any) => s + (h.vart || 0), 0);
  }
  get vanJavasolt(): boolean { return (this.adat?.honapok || []).some((h: any) => h.javasolt > 0); }

  /* ── Létszám felülírása ── */
  felulirKulcs(j: any, idoszak: string) { return `${j.id}|${idoszak}`; }
  felulirNyit(j: any, idoszak: string, jelenlegi: number) {
    this.felulirNyitva = this.felulirKulcs(j, idoszak);
    this.felulirErtek = jelenlegi;
    this.felulirMegj = '';
  }
  async felulirMent(j: any, idoszak: string, torol = false) {
    try {
      await this.post(`/api/normativa/felulir/${this.intezmeny}`, {
        jogcim_id: j.id, idoszak, letszam: torol ? null : this.felulirErtek, megjegyzes: this.felulirMegj,
      });
      this.felulirNyitva = null;
      this.jelez(torol ? 'A számolt létszám visszaállítva.' : 'A létszám felülírva.');
      await this.frissit();
    } catch (e: any) { this.hiba = e?.error?.error || 'Nem sikerült menteni.'; }
  }

  /* ── Jogcímek szerkesztése ── */
  ujJogcim() {
    const max = Math.max(0, ...(this.adat?.jogcimek || []).map((j: any) => Number(j.sorrend) || 0));
    this.szerk = { ev: this.ev, nev: '', kod: '7/1.1.2', csoport: 'kvtv', modszer: 'pedagogus', forras: 'tanulo', fajlagos: null,
      oszto: 11.8, evfolyam_tol: 1, evfolyam_ig: 8, sni_szorzo: 2, munkakor_szuro: '', f_tenyezo: null, sorrend: max + 10, aktiv: true };
  }
  jogcimSzerk(j: any) {
    this.szerk = { ...j, ev: this.ev,
      hat_letszam_1_8: j.hatarozat?.letszam_1_8 ?? null, hat_letszam_9_12: j.hatarozat?.letszam_9_12 ?? null,
      hat_osszeg_1_8: j.hatarozat?.osszeg_1_8 ?? null, hat_osszeg_9_12: j.hatarozat?.osszeg_9_12 ?? null };
  }
  get alapJeloltek(): any[] { return (this.adat?.jogcimek || []).filter((j: any) => j.id !== this.szerk?.id && j.forras !== 'jogcim'); }

  async jogcimMent() {
    try {
      await this.post(`/api/normativa/jogcim/${this.intezmeny}`, this.szerk);
      this.szerk = null;
      this.jelez('A jogcím mentve.');
      await this.frissit();
    } catch (e: any) { this.hiba = e?.error?.error || 'Nem sikerült menteni.'; }
  }
  async jogcimTorol(j: any) {
    if (!confirm(`Törlöd a(z) „${j.nev}” jogcímet (${this.ev})?`)) return;
    try {
      await firstValueFrom(this.http.delete<any>(`/api/normativa/jogcim/${j.id}/${this.intezmeny}`));
      this.jelez('A jogcím törölve.');
      await this.frissit();
    } catch (e: any) { this.hiba = e?.error?.error || 'Nem sikerült törölni.'; }
  }
  async evMasol() {
    if (!this.masolasForras) return;
    try {
      const r = await this.post(`/api/normativa/ev-masolas/${this.intezmeny}`, { forras_ev: this.masolasForras, cel_ev: this.ev });
      this.jelez(`${r.db} jogcím átmásolva – a fajlagos összegeket a(z) ${this.ev}. évi Kv.tv. szerint ellenőrizd.`);
      await this.frissit();
    } catch (e: any) { this.hiba = e?.error?.error || 'Nem sikerült a másolás.'; }
  }
  async sablon() {
    try {
      const r = await this.post(`/api/normativa/sablon/${this.intezmeny}`, { ev: this.ev });
      this.jelez(`${r.db} kiinduló jogcím betöltve – ellenőrizd a fajlagos összegeket és a létszám-forrásokat.`);
      await this.frissit();
    } catch (e: any) { this.hiba = e?.error?.error || 'Nem sikerült a betöltés.'; }
  }
  async hatarozatMent(szoveg: string) {
    try {
      await this.post(`/api/normativa/ev/${this.intezmeny}`, { ev: this.ev, hatarozat: szoveg, megjegyzes: this.adat?.ev_megjegyzes || null });
      this.jelez('Mentve.');
      await this.frissit();
    } catch (e: any) { this.hiba = e?.error?.error || 'Nem sikerült menteni.'; }
  }
}
